import React from 'react';
import { Box, Typography } from '@mui/material';
import { SectionCard, PillTabsWrapper, PillTab, StepRow, StepLeft, StepCircle, StepConnector, InfoBox } from './styles';
import { WindowsIcon, MacIcon, InfoCircleIcon, WarningTriangleIcon } from './icons';

function B({ children }) {
    return <strong style={{ fontWeight: 700, color: '#111827' }}>{children}</strong>;
}

const WINDOWS_STEPS = [
    <>Click <B>Download (.exe)</B> above and wait for the file to finish downloading.</>,
    <>Open your <B>Downloads</B> folder and double-click the installer.</>,
    <>If Windows shows a security prompt, choose <B>Yes</B> to allow the install.</>,
    <>Follow the installer and click <B>Finish</B>. A desktop shortcut is created automatically.</>,
    <>When it&apos;s exam time, open <B>Exam Browser</B> and sign in with your student credentials.</>,
];

const MAC_STEPS = [
    <>Click <B>Download (.dmg)</B> above and wait for the download to complete.</>,
    <>Open the downloaded <B>.dmg</B> file from your Downloads folder.</>,
    <>Drag the <B>Exam Browser</B> icon into your <B>Applications</B> folder.</>,
    <>Open Applications and launch the app. If macOS warns it&apos;s from the internet, choose <B>Open</B>.</>,
    <>Grant <B>Camera</B> and <B>Screen Recording</B> permission when prompted, then sign in.</>,
];

function StepList({ steps }) {
    return (
        <Box>
            {steps.map((step, i) => {
                const isLast = i === steps.length - 1;
                return (
                    <StepRow key={i} sx={{ mb: isLast ? 0 : 0.5 }}>
                        <StepLeft>
                            <StepCircle aria-hidden="true">{i + 1}</StepCircle>
                            {!isLast && <StepConnector />}
                        </StepLeft>
                        <Box sx={{ pt: 0.6, pb: isLast ? 0 : 2 }}>
                            <Typography variant="body2" lineHeight={1.65} color="#374151">
                                {step}
                            </Typography>
                        </Box>
                    </StepRow>
                );
            })}
        </Box>
    );
}

export default function InstallationInstructions({ data, activeTab, onTabChange }) {
    const tabs = [];
    if (data.windows) tabs.push('windows');
    if (data.mac) tabs.push('mac');
    if (tabs.length === 0) return null;

    const currentTab = tabs[activeTab] || tabs[0];

    return (
        <SectionCard component="section" aria-labelledby="install-title">
            <Typography id="install-title" variant="h6" fontWeight={700} mb={0.5}>
                How to install
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Pick your system and follow the steps. You only need to do this once.
            </Typography>

            {tabs.length > 1 && (
                <PillTabsWrapper role="tablist" aria-label="Installation tabs">
                    {tabs.map((t, i) => (
                        <PillTab
                            key={t}
                            active={activeTab === i ? 'true' : 'false'}
                            onClick={() => onTabChange(i)}
                            role="tab"
                            aria-selected={activeTab === i}
                            id={`tab-${t}`}
                            aria-controls={`tabpanel-${t}`}
                            disableRipple={activeTab === i}
                        >
                            {t === 'windows' ? <WindowsIcon size={14} /> : <MacIcon size={14} />}
                            {t === 'windows' ? 'Windows' : 'macOS'}
                        </PillTab>
                    ))}
                </PillTabsWrapper>
            )}

            <Box role="tabpanel" id={`tabpanel-${currentTab}`} aria-labelledby={`tab-${currentTab}`}>
                {currentTab === 'windows' ? (
                    <>
                        <StepList steps={WINDOWS_STEPS} />
                        <InfoBox variant="info" sx={{ mt: 3 }}>
                            <InfoCircleIcon />
                            <Typography variant="body2" color="#1E40AF">
                                <strong>Install the day before.</strong> Don&apos;t wait until your exam starts — get it set up early so any issues are sorted with time to spare.
                            </Typography>
                        </InfoBox>
                    </>
                ) : (
                    <>
                        <StepList steps={MAC_STEPS} />
                        <InfoBox variant="warning" sx={{ mt: 3 }}>
                            <WarningTriangleIcon />
                            <Typography variant="body2" color="#92400E">
                                <strong>Permissions matter.</strong> If you skip the Camera or Screen Recording prompt, the exam won&apos;t launch. Re-enable them under System Settings → Privacy &amp; Security.
                            </Typography>
                        </InfoBox>
                    </>
                )}
            </Box>
        </SectionCard>
    );
}
